import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import { notificationService } from '@/services/notification/notification.client';
import { UpdateContractAdminData, UpdateContractAdminDataVariables } from
  '@/graphql/contracts/_generated_/updateContractAdminData.mutation';
import UpdateContractAdminDataMutation from '@/graphql/contracts/updateContractAdminData.mutation.gql';
import { UpdateContractAdminDataInputType } from '@/graphql/contracts/_generated_/contracts';
import { GetContractDetails, GetContractDetailsVariables } from
  '@/graphql/contracts/_generated_/getContractDetails.query';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import { fieldVerificationRefreshTrigger } from '@/pages/ContractDetails/ContractIq/ContractIq.state';

export default function useUpdateContractAdminData(contractId: UUID, variableSelected: boolean) {
  const setRefreshTrigger = useSetRecoilState(fieldVerificationRefreshTrigger);

  const [_updateContractAdminData, { loading }] = useMutation<
  UpdateContractAdminData, UpdateContractAdminDataVariables>(
    UpdateContractAdminDataMutation,
    {
      client: apolloClientContracts,
      refetchQueries: [
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        },
        ...(variableSelected ? [
          {
            query: GetPaginatedFilteredVariableContractLinesQuery,
            variables: {
              filter: { headerPrimaryKey: { eq: contractId } },
              first: ALL_ITEMS,
            }
          }
        ] : [])
      ],

      update: (_, { data }) => {
        if (!data?.updateContractAdminData) return;

        apolloClientContracts.writeQuery<GetContractDetails, GetContractDetailsVariables>({
          query: GetContractDetailsQuery,
          variables: { contractId },
          data: {
            contract: data.updateContractAdminData?.contract
          }
        });
      },
      onError: (err) => {
        notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        });
      },
      onCompleted: () => {
        notificationService.open({
          title: 'Update Contract headers',
          text: 'Contract headers successfully updated',
          type: 'success'
        });
        setRefreshTrigger((prev) => prev + 1);
      },
    },
  );

  const updateContractAdminData = (contractData: UpdateContractAdminDataInputType) => {
    return _updateContractAdminData({
      variables: {
        input: contractData
      }
    }).then(res => res.data ?? null);
  };

  return {
    updateContractAdminData,
    isLoadingUpdateContractAdminData: loading
  } as const;
}
